import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import cn from "classnames";
import { useFormik } from "formik";
import { object, number } from "yup";

import { createFormFieldValidation, isRegularCategory } from "./helpers";
import { Modal } from "components/ui-kit/Modal/Modal";
import { Dropdown } from "components/ui-kit/Dropdown";
// import { RadioGroup } from "components/ui-kit/Radio";
import { Input } from "components/ui-kit/Input";
import { constants } from "utilities/constants";
import { helperFunctions } from "utilities/helper-functions";
import { tradegroupAPI } from "api/tradegroup-api";

import "./styles.scss";

const { RequestTypes } = constants;

const DAS_MAX_LOSS_FIELDS = ["auto_liquidate", "autoliq", "max_loss"];

const getRiskSettingValue = (riskSettings, candidates = []) => {
  const normalizedCandidates = Array.isArray(candidates) ? candidates : [candidates];
  for (const field of normalizedCandidates) {
    const value = riskSettings?.[field];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
};

createFormFieldValidation("riskSettingValue");

const CreateRiskSettingsModal = ({
  closeModal,
  updateCategory,
  categories,
  traderIdsList,
  activeCategory,
  updateTraderIds,
  activeTraderIds,
  TGAccountsList,
  submitModal,
}) => {
  const { riskSettingMetadata } =
    useSelector((state) => state.tradeGroup) || [];

  // const [selectedRequestDate, setSelectedRequestDate] = useState(null);
  const [isChangedCategory, setChangedCategory] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [activeRiskSetting, setRiskSetting] = useState(null);
  // Doesn't work for group members — uncomment when RM EOD is fixed.
  // EOD is not recalculated in RM; RM only recalculates when triggered by AccountIO
  // for a particular trader.
  // const [eod, setEod] = useState([
  //   { label: "Permanent", checked: false, value: "perm" },
  //   { label: "Reset end of day (EOD)", checked: false, value: "eod" },
  // ]);
  const [localActiveTraderIds, setLocalActiveTraderIds] =
    useState(activeTraderIds);
  /** RM-backed rows when applicationID is passed to risk-setting-metadata (source of truth for param names). */
  const [accountRiskMetadata, setAccountRiskMetadata] = useState(null);
  const [riskMetadataLoading, setRiskMetadataLoading] = useState(false);

  // Update local state when activeTraderIds prop changes
  useEffect(() => {
    setLocalActiveTraderIds(activeTraderIds);
  }, [activeTraderIds]);

  const defaultTraderId =
    localActiveTraderIds?.length === 1 ? localActiveTraderIds[0] : "";

  const currentTraderTradingPlatform = () => {
    let currentTraderID;
    if (!!defaultTraderId) {
      currentTraderID = defaultTraderId;
    } else {
      currentTraderID = localActiveTraderIds[0];
    }
    const foundTrader = TGAccountsList?.find(
      (t) => t.reference.client === currentTraderID
    );
    if (foundTrader) {
      return foundTrader.software;
    }
    return "";
  };

  const platform = currentTraderTradingPlatform();

  const foundAccount = useMemo(() => {
    if (defaultTraderId.length && TGAccountsList) {
      return TGAccountsList.find((a) => a.reference.client === defaultTraderId);
    }
  }, [TGAccountsList, defaultTraderId]);

  const baseRiskMetadata = accountRiskMetadata ?? riskSettingMetadata;

  const updatedRiskSettingMetadata = baseRiskMetadata
    ?.filter((item) => {
      // Exclude items where default is 0
      if (item.settings.default === 0) return false;
  
      // If availablePlatforms exists, include item only if current platform is available
      if (item.settings.availablePlatforms) {
        return item.settings.availablePlatforms.includes(platform);
      }
  
      // Include the item if availablePlatforms doesn't exist
      return true;
    })
    .map((item) => {
      // Create a new item with the 'value' property
      let newItem = { ...item, value: item.name };
  
      // If platformToLabel exists and matches the current platform, update the label
      if (item.settings.platformToLabel && item.settings.platformToLabel[platform]) {
        newItem.label = item.settings.platformToLabel[platform];
      }
      // DAS may store max loss under several keys; keep request `field` = RM/metadata `name` (never force auto_liquidate).
      const dasMaxLossFamily =
        platform === "DAS" &&
        (item.name === "max_loss" || item.name === "auto_liquidate");
      newItem = {
        ...newItem,
        valueCandidates: dasMaxLossFamily ? DAS_MAX_LOSS_FIELDS : [item.name],
      };

      return newItem;
    });
  

  const defaultCategory = categories?.find(
    (category) => category.value === activeCategory.value
  );


  const RiskSettingsSchema = object({
    updatedValue: number("Only positive integer numbers are allowed")
      .required("Required field")
      .positive("Only positive integer numbers are allowed")
      .integer("Only positive integer numbers are allowed")
      .typeError("Only positive integer numbers are allowed")
      .riskSettingValue(activeRiskSetting),
  });

  const formik = useFormik({
    initialValues: {
      updatedValue: "",
    },
    validationSchema: RiskSettingsSchema,
    onSubmit: (values) => handleFormSubmit(values),
  });

  useEffect(() => {
    if (!foundAccount?._id) {
      setAccountRiskMetadata(null);
      setRiskMetadataLoading(false);
      return;
    }
    let cancelled = false;
    setRiskMetadataLoading(true);
    (async () => {
      try {
        const res = await tradegroupAPI.getRSMetadata(foundAccount._id);
        if (cancelled) {
          return;
        }
        if (
          res.status !== 200 ||
          !res.data?.success ||
          !Array.isArray(res.data.items)
        ) {
          setAccountRiskMetadata(null);
          return;
        }
        setAccountRiskMetadata(res.data.items);
      } catch {
        if (!cancelled) {
          setAccountRiskMetadata(null);
        }
      } finally {
        if (!cancelled) {
          setRiskMetadataLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [foundAccount?._id]);

  const currentRiskValue = useMemo(() => {
    return getRiskSettingValue(
      foundAccount?.riskSettings,
      activeRiskSetting?.valueCandidates
    );
  }, [foundAccount, activeRiskSetting]);

  const handleFormSubmit = (values) => {
    localActiveTraderIds.forEach((AT) => {
      const activeTrader =
        localActiveTraderIds.length === 1
          ? foundAccount
          : TGAccountsList.find((a) => a.reference.client === AT);

      // const isEodPayload = selectedRequestDate?.value === "eod";
      const metadataRows = accountRiskMetadata ?? riskSettingMetadata ?? [];
      const rmListsAutoLiquidate = metadataRows.some(
        (row) => row?.name === "auto_liquidate"
      );
      let extField = activeRiskSetting.name;
      if (extField === "max_loss" && rmListsAutoLiquidate) {
        extField = "auto_liquidate";
      }
      const body = {
        type: RequestTypes.RISKSETTING,
        label: activeRiskSetting.label,
        ext: {
          field: extField,
          oldValue: getRiskSettingValue(
            activeTrader?.riskSettings,
            activeRiskSetting?.valueCandidates
          ),
          value: values.updatedValue,
          condition: null,
          overrideCondition: null,
        },
        eod: false, // isEodPayload — see commented EOD UI/state above
      };

      console.log("[TG-RS-CREATE] Submit risk request", {
        platform,
        traderRef: AT,
        selectedRiskSetting: activeRiskSetting?.label,
        field: body?.ext?.field,
        oldValue: body?.ext?.oldValue,
        value: body?.ext?.value,
        condition: body?.ext?.condition,
        eod: body?.eod,
      });

      submitModal({
        body,
        type: RequestTypes.RISKSETTING,
        traderRef: AT,
      });
    });
  };

  const onTraderIdChange = (selectedTraderId) => {
    setLocalActiveTraderIds([selectedTraderId]);
    updateTraderIds(localActiveTraderIds, true);
  };

  const handleRiskSettingChange = (riskSetting) => {
    setRiskSetting(() =>
      updatedRiskSettingMetadata?.find((item) => item.name === riskSetting)
    );
    setDisabled(false);
    // setEod((oldVal) => {
    //   const newEod = [...oldVal];
    //   newEod[0].checked = true;
    //   return newEod;
    // });

    document
      .getElementById("category-value-field")
      .dispatchEvent(new Event("change"));
    setChangedCategory(true);
  };

  const handleInputBlur = (e) => {
    const oldVal = formik.values.updatedValue;
    formik.setFieldValue(
      "updatedValue",
      helperFunctions.roundToNearest(
        oldVal,
        isRegularCategory(activeRiskSetting) ? 100 : 10
      )
    );
  };

  useEffect(() => {
    if (isChangedCategory) {
      formik.validateForm();
      setChangedCategory(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChangedCategory]);

  // useEffect(() => {
  //   setSelectedRequestDate(() => eod?.find((item) => item.checked));
  // }, [eod]);

  return (
    <div className="modal-window">
      <Modal
        className="risk-settings-modal cabinet-modals"
        overlayClassName="request-overlay"
        styleType="zimtra-primary"
        isOpen={true}
        hideBlur
        hasCloseIcon={false}
        onClickOutside={closeModal}
      >
        <Modal.Header
          className="modal-inner-header"
          onClose={closeModal}
          title={<h2 className="zimtra-h2">New Request</h2>}
        />
        <Modal.Body className="modal-inner-body">
          <div className="d-flex align-items-top modal-row">
            <div className="left-side">Category:</div>
            <div>
              <Dropdown
                options={categories}
                defaultValue={defaultCategory.value}
                className="request-dropdown select-category-dropdown"
                placeholder="Select category"
                onChange={updateCategory}
              />
            </div>
          </div>
          <div className="d-flex align-items-top modal-row">
            <div className="left-side">TraderID:</div>
            {localActiveTraderIds?.length > 1 ? (
              <div className="left-side">{localActiveTraderIds.join(", ")}</div>
            ) : (
              <div>
                <Dropdown
                  options={traderIdsList}
                  defaultValue={defaultTraderId}
                  className="request-dropdown select-category-dropdown"
                  placeholder="Select TraderID"
                  onChange={(value) => onTraderIdChange(value)}
                />
              </div>
            )}
          </div>
          <hr />
          {!!platform && (
            <div className="mt-4">
              Chosen Platform: {platform}
            </div>
          )}
          <form onSubmit={formik.handleSubmit}>
            <div
              className={cn("d-flex align-items-top modal-row", {
                "disable-row": !!foundAccount?._id && riskMetadataLoading,
              })}
            >
              <Dropdown
                options={updatedRiskSettingMetadata}
                className="request-dropdown select-risk-settings-dropdown"
                placeholder={
                  foundAccount?._id && riskMetadataLoading
                    ? "Loading risk settings…"
                    : "Select a Risk Settings"
                }
                disabled={!!foundAccount?._id && riskMetadataLoading}
                onChange={handleRiskSettingChange}
              />
            </div>
            <div className="d-flex align-items-top current-price-row modal-row">
              <div className="left-side">current</div>
              <div>
                {activeRiskSetting && foundAccount && currentRiskValue !== undefined
                  ? helperFunctions
                      .currencyFormatter()
                      .format(currentRiskValue)
                      .replace(/\D00$/, "")
                  : "N/A"}
              </div>
            </div>
            <div
              className={cn(
                "d-flex align-items-top change-price-row modal-row",
                {
                  "disable-row": disabled,
                }
              )}
            >
              <div className="change-text left-side">change to</div>
              <div
                className={cn("change-value", {
                  "error-field": formik.errors.updatedValue,
                })}
              >
                <Input
                  labelClassName="cabinet-input"
                  variant="zimtra-primary"
                  placeholder="New Value"
                  size="short"
                  id="category-value-field"
                  name="updatedValue"
                  onChange={formik.handleChange}
                  onBlur={handleInputBlur}
                  value={formik.values.updatedValue}
                />
                <Tooltip
                  placement="bottom-end"
                  isClosable={false}
                  anchorSelect={`#anchor-tooltip-id`}
                >
                  i
                  {activeRiskSetting ? (
                    <span>
                      Value is rounded to the nearest increment of &nbsp;
                      {isRegularCategory(activeRiskSetting) ? 100 : 10}
                    </span>
                  ) : null}
                </Tooltip>
                {formik.errors.updatedValue ? (
                  <div className="error-text">{formik.errors.updatedValue}</div>
                ) : null}
              </div>
            </div>
            {/* EOD option — doesn't work for group members; uncomment when RM EOD is fixed.
                EOD is not recalculated in RM; RM only recalculates when triggered by AccountIO
                for a particular trader.
            <div
              className={cn(
                "d-flex align-items-top type-eod-row radio-group-container modal-row",
                {
                  "disable-row": disabled,
                }
              )}
            >
              <RadioGroup
                data={eod}
                onChange={(item) => setSelectedRequestDate(item)}
              />
            </div>
            */}
          </form>
          <hr />
        </Modal.Body>
        <Modal.Footer className="modal-inner-footer d-flex justify-content-between">
          <button
            type="button"
            className="main-button"
            disabled={
              disabled ||
              !formik.isValid ||
              (!!foundAccount?._id && riskMetadataLoading)
            }
            onClick={formik.handleSubmit}
          >
            Create
          </button>
          <button
            type="button"
            className="main-button cancel-button"
            onClick={closeModal}
          >
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CreateRiskSettingsModal;
